import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rawFetch } from "@/lib/api/client";
import { PageHeader, ErrorBox, DataTable, StatusPill, EmptyState } from "@/components/common";
import { useApp } from "@/lib/store";
import { fmtDate, shortId } from "@/lib/utils";
import { PricingEngagement } from "@/components/PricingEngagement";
import { CheckCircle2, Crown, Mail, Send, Sparkles, Trash2, UserPlus, Users as UsersIcon, X } from "lucide-react";

const ROLES = ["owner", "admin", "editor", "viewer"] as const;
type Role = (typeof ROLES)[number];

interface Org {
  id: string; name: string; slug: string; schema_name: string;
  plan: "free" | "pro" | "enterprise"; plan_label: string;
  industry: string; billing_email: string;
  trial_ends_at: string | null;
  limits: Record<string, number>;
}

interface Membership {
  id: string; user: number; workspace: string; role: Role;
  user_detail: { id: number; username: string; email: string; full_name: string; is_active: boolean };
  workspace_slug: string; workspace_name: string;
  created_at: string;
}

interface Invitation {
  id: string; workspace: string; workspace_name: string; email: string;
  role: Role; status: string; token: string; created_at: string;
  expires_at: string | null; invite_url: string;
}

interface Usage {
  plan: { id: string; label: string; limits: Record<string, number> };
  seats: { used: number; limit: number };
  workspaces: { used: number; limit: number };
  datasets: { used: number; limit: number };
  monthly_queries: { used: number; limit: number };
  monthly_pipeline_runs: { used: number; limit: number };
  max_pack_installs: { used: number; limit: number };
}

interface Plan { id: string; label: string; limits: Record<string, number> }

function fmtLimit(n: number): string {
  return n === 0 ? "Unlimited" : n.toLocaleString();
}

function MeterRow({ label, used, limit }: { label: string; used: number; limit: number }) {
  const unlimited = limit === 0;
  const pct = unlimited || used < 0 ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const bar =
    pct >= 90 ? "bg-bad" :
    pct >= 70 ? "bg-warn" :
                "bg-accent";
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="label-micro">{label}</span>
        <span className="stat-mono text-[13px]">
          {used < 0 ? "—" : used.toLocaleString()}{" / "}
          <span className="text-muted">{fmtLimit(limit)}</span>
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-border/60">
        <div className={`h-full ${bar}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function Users() {
  const ws = useApp((s) => s.workspaceId);
  const workspaces = useApp((s) => s.workspaces);
  const me = useApp((s) => s.user);
  const qc = useQueryClient();

  const currentWorkspace = workspaces.find((w) => w.id === ws);
  const orgId = currentWorkspace?.organization;

  // ---------- queries
  const orgsQ = useQuery<{ results: Org[] } | Org[]>({
    queryKey: ["organizations"],
    queryFn: () => rawFetch("/iam/organizations/?page_size=50"),
  });
  const orgs: Org[] = useMemo(() => {
    const data = orgsQ.data as any;
    return Array.isArray(data) ? data : (data?.results ?? []);
  }, [orgsQ.data]);
  const org = orgs.find((o) => o.id === orgId) ?? orgs[0];

  const usageQ = useQuery<Usage>({
    queryKey: ["usage", org?.id],
    enabled: !!org,
    queryFn: () => rawFetch(`/iam/organizations/${org!.id}/usage/`),
  });

  const memQ = useQuery<{ results: Membership[] }>({
    queryKey: ["memberships", org?.id],
    enabled: !!org,
    queryFn: async () => {
      // The backend already scopes memberships to the orgs the caller
      // belongs to, so a single call returns everything we may display.
      // We then narrow to the active org by workspace id WHEN the workspace
      // list is available; otherwise we show what the server returned so an
      // owner is never hidden by a stale/empty client workspace cache.
      const r = await rawFetch<{ results: Membership[] }>(`/iam/memberships/?page_size=200`);
      const wsIds = new Set(workspaces.filter((w) => w.organization === org!.id).map((w) => w.id));
      const results = wsIds.size ? r.results.filter((m) => wsIds.has(m.workspace)) : r.results;
      return { results };
    },
  });

  const invQ = useQuery<{ results: Invitation[] }>({
    queryKey: ["invitations", org?.id],
    enabled: !!org,
    queryFn: async () => {
      const r = await rawFetch<{ results: Invitation[] }>(`/iam/invitations/?status=pending&page_size=200`);
      const wsIds = new Set(workspaces.filter((w) => w.organization === org!.id).map((w) => w.id));
      const results = wsIds.size ? r.results.filter((i) => wsIds.has(i.workspace)) : r.results;
      return { results };
    },
  });

  const plansQ = useQuery<Plan[]>({
    queryKey: ["plans"],
    queryFn: () => rawFetch("/iam/plans/"),
  });

  // ---------- mutations
  const inviteM = useMutation({
    mutationFn: (body: { email: string; workspace: string; role: Role }) =>
      rawFetch<Invitation>("/iam/invitations/", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invitations", org?.id] }),
  });
  const revokeM = useMutation({
    mutationFn: (id: string) => rawFetch(`/iam/invitations/${id}/revoke/`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invitations", org?.id] }),
  });
  const roleM = useMutation({
    mutationFn: ({ id, role }: { id: string; role: Role }) =>
      rawFetch(`/iam/memberships/${id}/`, { method: "PATCH", body: JSON.stringify({ role }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["memberships", org?.id] }),
  });
  const removeM = useMutation({
    mutationFn: (id: string) => rawFetch(`/iam/memberships/${id}/`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["memberships", org?.id] }),
  });
  const planM = useMutation({
    mutationFn: (planId: string) => rawFetch<Org>(`/iam/organizations/${org!.id}/set-plan/`, {
      method: "POST", body: JSON.stringify({ plan: planId }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["organizations"] });
      qc.invalidateQueries({ queryKey: ["usage", org?.id] });
    },
  });

  // ---------- invite form
  const orgWorkspaces = workspaces.filter((w) => w.organization === org?.id);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [engagePlan, setEngagePlan] = useState<"pro" | "enterprise" | null>(null);
  const [iEmail, setIEmail] = useState("");
  const [iRole, setIRole] = useState<Role>("editor");
  const [iWs, setIWs] = useState<string>(ws ?? "");

  function submitInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!iEmail || !iWs) return;
    inviteM.mutate({ email: iEmail, workspace: iWs, role: iRole },
      { onSuccess: () => { setIEmail(""); setInviteOpen(false); } });
  }

  // ---------- render
  if (!org) {
    return (
      <>
        <PageHeader title="Users" subtitle="Members, roles, and plan." />
        <EmptyState title="No organization yet"
          hint="Create one from /signup or pick a workspace from the header." />
      </>
    );
  }

  const isOwner = memQ.data?.results.some(
    (m) => m.user === me?.id && (m.role === "owner" || m.role === "admin"),
  ) || me?.is_superuser
    // Fallback: the account that created the org (its billing contact) is the
    // owner even if the membership list momentarily fails to load.
    || (!!me?.email && me.email.toLowerCase() === (org.billing_email || "").toLowerCase());

  return (
    <>
      <PageHeader
        title="Users & Plan"
        subtitle={`Manage ${org.name} — ${memQ.data?.results.length ?? 0} member(s)`}
        actions={
          isOwner ? (
            <button className="btn-primary" onClick={() => setInviteOpen(true)}>
              <UserPlus size={14} /> Invite
            </button>
          ) : undefined
        }
      />

      {/* --- Plan + Usage --------------------------------------------- */}
      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-[1fr_2fr]">
        <div className="card">
          <div className="mb-2 flex items-center gap-2 text-sm font-medium">
            <Crown size={16} className="text-accent" /> Current plan
          </div>
          <div className="mb-1 stat-mono text-2xl">{org.plan_label}</div>
          {org.trial_ends_at && (
            <div className="mb-3 text-xs text-warn">
              Trial ends {fmtDate(org.trial_ends_at)}
            </div>
          )}
          {isOwner && (
            <div className="mt-3 flex flex-wrap gap-2">
              {plansQ.data?.map((p) => {
                const isCurrent = p.id === org.plan;
                const isPaid = p.id === "pro" || p.id === "enterprise";
                return (
                  <button
                    key={p.id}
                    className={isCurrent ? "btn-primary" : "btn"}
                    disabled={isCurrent || planM.isPending}
                    onClick={() =>
                      isPaid
                        ? setEngagePlan(p.id as "pro" | "enterprise")
                        : planM.mutate(p.id)
                    }
                    title={
                      isCurrent ? "Currently active"
                        : isPaid ? `Talk to us about ${p.label}`
                        : `Switch to ${p.label}`
                    }
                  >
                    {isCurrent && <CheckCircle2 size={14} />}
                    {p.label}
                    {!isCurrent && isPaid && <span className="ml-1 text-[10px] opacity-70">· talk to us</span>}
                  </button>
                );
              })}
            </div>
          )}
          {isOwner && (
            <p className="mt-3 text-[11px] leading-relaxed text-muted">
              Pro and Enterprise are priced on the value they create, not per seat —
              so they start with a short scoping conversation and an architecture review.
            </p>
          )}
          {planM.isError && <div className="mt-2"><ErrorBox error={planM.error} /></div>}
        </div>

        <div className="card">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium">
            <Sparkles size={16} className="text-accent" /> Usage this month
          </div>
          {usageQ.isLoading && <div className="text-xs text-muted">Loading…</div>}
          {usageQ.data && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <MeterRow label="Seats"             used={usageQ.data.seats.used}             limit={usageQ.data.seats.limit} />
              <MeterRow label="Workspaces"        used={usageQ.data.workspaces.used}        limit={usageQ.data.workspaces.limit} />
              <MeterRow label="Datasets"          used={Math.max(0, usageQ.data.datasets.used)}          limit={usageQ.data.datasets.limit} />
              <MeterRow label="Pack installs"     used={Math.max(0, usageQ.data.max_pack_installs.used)} limit={usageQ.data.max_pack_installs.limit} />
              <MeterRow label="Queries / mo"      used={Math.max(0, usageQ.data.monthly_queries.used)}   limit={usageQ.data.monthly_queries.limit} />
              <MeterRow label="Pipeline runs / mo" used={Math.max(0, usageQ.data.monthly_pipeline_runs.used)} limit={usageQ.data.monthly_pipeline_runs.limit} />
            </div>
          )}
          <div className="mt-3 text-[11px] text-muted">
            Tenant-scoped meters (datasets, queries, runs) read from the active workspace's
            schema. They show <code>—</code> when the workspace has no data yet.
          </div>
        </div>
      </div>

      {/* --- Members --------------------------------------------------- */}
      <h2 className="mb-2 text-sm font-medium text-muted flex items-center gap-1">
        <UsersIcon size={14} /> Members
      </h2>
      {memQ.isError && <ErrorBox error={memQ.error} />}
      <DataTable<Membership>
        rows={memQ.data?.results ?? []}
        empty="No members yet."
        cols={[
          { header: "User", cell: (m) => (
            <div>
              <div className="font-medium">{m.user_detail?.full_name || m.user_detail?.username}</div>
              <div className="text-xs text-muted">{m.user_detail?.email}</div>
            </div>) },
          { header: "Workspace", cell: (m) => (
            <div className="text-sm">
              {m.workspace_name}
              <div className="text-xs text-muted">{m.workspace_slug}</div>
            </div>) },
          { header: "Role", cell: (m) => (
            isOwner && m.user !== me?.id ? (
              <select
                className="input max-w-[120px]"
                value={m.role}
                onChange={(e) => roleM.mutate({ id: m.id, role: e.target.value as Role })}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            ) : <StatusPill status={m.role} />
          ) },
          { header: "Joined", cell: (m) => fmtDate(m.created_at) },
          { header: "", width: "1%", cell: (m) => (
            isOwner && m.user !== me?.id ? (
              <button
                className="btn-ghost text-bad"
                title="Remove member"
                onClick={() => {
                  if (confirm(`Remove ${m.user_detail?.username} from ${m.workspace_name}?`))
                    removeM.mutate(m.id);
                }}>
                <Trash2 size={14} />
              </button>
            ) : null
          ) },
        ]}
      />

      {/* --- Pending invites ------------------------------------------ */}
      <h2 className="mb-2 mt-6 text-sm font-medium text-muted flex items-center gap-1">
        <Mail size={14} /> Pending invitations
      </h2>
      {invQ.isError && <ErrorBox error={invQ.error} />}
      <DataTable<Invitation>
        rows={invQ.data?.results ?? []}
        empty="No pending invitations."
        cols={[
          { header: "Email",     cell: (i) => i.email },
          { header: "Workspace", cell: (i) => i.workspace_name },
          { header: "Role",      cell: (i) => <StatusPill status={i.role} /> },
          { header: "Token",     cell: (i) => <code className="font-mono text-xs">{shortId(i.token)}</code> },
          { header: "Sent",      cell: (i) => fmtDate(i.created_at) },
          { header: "Link",      cell: (i) => (
            <button
              className="btn"
              onClick={() => navigator.clipboard.writeText(window.location.origin + i.invite_url)}
              title="Copy invite link"
            >
              Copy link
            </button>) },
          { header: "", width: "1%", cell: (i) => (
            isOwner ? (
              <button className="btn-ghost text-bad" title="Revoke invitation"
                onClick={() => revokeM.mutate(i.id)}>
                <X size={14} />
              </button>
            ) : null
          ) },
        ]}
      />

      {/* --- Pricing engagement (value-based, Palantir-style) --------- */}
      {engagePlan && (
        <PricingEngagement
          open={!!engagePlan}
          plan={engagePlan}
          orgName={org.name}
          industry={org.industry}
          contactEmail={me?.email ?? org.billing_email}
          onClose={() => setEngagePlan(null)}
        />
      )}

      {/* --- Invite modal --------------------------------------------- */}
      {inviteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#05070b]/80 p-4 backdrop-blur-sm animate-fadeIn"
             onClick={() => setInviteOpen(false)} role="dialog" aria-modal="true" aria-label="Invite a member">
          <form
            className="w-full max-w-md overflow-hidden rounded-2xl border border-border bg-panel shadow-2xl animate-slideIn"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitInvite}
          >
            <div className="relative overflow-hidden bg-gradient-to-br from-[#0b1220] via-[#0d1526] to-[#070a12] px-6 py-5">
              <div
                className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full opacity-30 blur-2xl"
                style={{ background: "radial-gradient(closest-side, hsl(var(--accent)), transparent)" }}
                aria-hidden
              />
              <button type="button" onClick={() => setInviteOpen(false)} aria-label="Close"
                      className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-lg text-white/60 transition hover:bg-white/10 hover:text-white">
                <X size={16} />
              </button>
              <div className="relative inline-flex items-center gap-2 text-[15px] font-bold tracking-[0.14em] text-white">
                <UserPlus size={16} className="text-[hsl(var(--accent))]" /> ONTOLIGENT<span className="text-good">.</span>
              </div>
              <h3 className="relative mt-3 text-xl font-semibold tracking-tight text-white">Invite a member</h3>
              <p className="relative mt-1.5 text-[13px] leading-relaxed text-white/65">
                Add a teammate to a workspace with a scoped role.
              </p>
            </div>
            <div className="p-6">
              <label className="label">Email</label>
              <input className="input mb-3" type="email" required autoFocus
                     value={iEmail} onChange={(e) => setIEmail(e.target.value)} />
              <label className="label">Workspace</label>
              <select className="input mb-3" required
                      value={iWs} onChange={(e) => setIWs(e.target.value)}>
                <option value="">Select…</option>
                {orgWorkspaces.map((w) => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
              <label className="label">Role</label>
              <select className="input mb-3" value={iRole}
                      onChange={(e) => setIRole(e.target.value as Role)}>
                {ROLES.filter((r) => r !== "owner").map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              {inviteM.isError && <ErrorBox error={inviteM.error} />}
              <button className="btn-primary w-full justify-center py-2.5" type="submit" disabled={inviteM.isPending}>
                <Send size={14} /> {inviteM.isPending ? "Sending…" : "Send invitation"}
              </button>
              <p className="mt-2.5 text-[11px] text-muted">
                Invitations expire after 14 days. The recipient signs up via the
                link — if they already have an account, they're added straight away.
              </p>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
